/**
 * Phase 1.15 v1 — Application Insights instrumentation entry point (frontend).
 *
 * Single import surface for client-side telemetry. Lazy-init once at app boot
 * via initTelemetry(); subsequent calls hit the already-initialized instance.
 *
 * v1 scope (per Phase 1.15 §0.5 operator narrowing 2026-05-19):
 *   - Init-only. Auto-tracked: page views, route changes, fetch/xhr requests,
 *     unhandled exceptions, performance metrics.
 *   - trackException() exposed for manual error capture (used sparingly —
 *     auto-tracking covers most cases).
 *   - setAuthenticatedUser() exposed for §6.2 full-identifiable binding
 *     (locked by operator 2026-05-19 for v1 office/managerial user set).
 *
 * Custom event emit infrastructure (§5 of the brief) is deferred to CM rollout
 * per the v1 scope decision; this module's surface is intentionally narrow.
 *
 * Connection string read from import.meta.env.APPLICATIONINSIGHTS_CONNECTION_STRING.
 * vite.config.ts extends envPrefix to expose `APPLICATIONINSIGHTS_*` to the
 * client bundle — the conn string is non-secret by design (App Insights browser
 * SDK is meant to use it directly; anyone can sniff it from a browser anyway).
 */

import { ApplicationInsights, type ICustomProperties } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;
let initialized = false;

export function initTelemetry(): void {
  if (initialized) return;
  initialized = true;

  const connectionString = import.meta.env.APPLICATIONINSIGHTS_CONNECTION_STRING as
    | string
    | undefined;

  if (!connectionString) {
    // Dev / test / preview-without-config — silent skip. Functions below no-op.
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableFetchTracking: false,
      autoTrackPageVisitTime: true,
    },
  });
  appInsights.loadAppInsights();
  appInsights.trackPageView();
}

/**
 * Bind subsequent telemetry events to a specific authenticated user.
 * Per §6.2 (operator-locked 2026-05-19): v1 office/managerial user set
 * uses full per-person identifiable binding. Call from the auth bootstrap
 * once whoami resolves.
 *
 * userId: the canonical UUID from `users.id` (NOT email/name).
 * tenantId: the canonical UUID from `tenants.id` (used as accountId for
 *   tenant-scoped queries in App Insights).
 */
export function setAuthenticatedUser(userId: string, tenantId: string): void {
  appInsights?.setAuthenticatedUserContext(userId, tenantId, /*storeInCookie*/ false);
}

export function clearAuthenticatedUser(): void {
  appInsights?.clearAuthenticatedUserContext();
}

export function trackException(error: Error, context?: ICustomProperties): void {
  appInsights?.trackException({ exception: error, properties: context });
}

/**
 * Test-only escape hatch — resets the module to its uninitialized state so
 * unit tests can verify idempotency + no-op-without-config without leaking
 * state between tests. Production code never calls this.
 */
export function __resetForTests(): void {
  appInsights = null;
  initialized = false;
}
